import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, ArrowUpRight, BellRing, Bot, Building2, Mail, PlugZap, Sparkles, X } from 'lucide-react';
import { ISLAND_GAP_PX, type AppNotification, type NotificationKind, type NotificationPrefs } from '@mailpilot/shared';
import { useAccountFilter } from '../../lib/accountFilter';
import { initials } from '../../lib/format';

// The island is black in both themes (like the hardware it imitates); only the icon tile is coloured.
const KIND: Record<NotificationKind, { label: string; icon: ReactNode; tile: string }> = {
  mail: { label: 'New email', icon: <Mail className="h-[18px] w-[18px]" />, tile: 'rgb(var(--primary))' },
  ai_draft: { label: 'AI draft ready', icon: <Sparkles className="h-[18px] w-[18px]" />, tile: 'linear-gradient(135deg, #8B5CF6, rgb(var(--primary)))' },
  ai_analysis: { label: 'AI insight', icon: <Bot className="h-[18px] w-[18px]" />, tile: 'linear-gradient(135deg, #A855F7, #6366F1)' },
  auto_reply: { label: 'Automation', icon: <BellRing className="h-[18px] w-[18px]" />, tile: '#059669' },
  reminder: { label: 'Reminder', icon: <BellRing className="h-[18px] w-[18px]" />, tile: '#D97706' },
  send_failed: { label: 'Send failed', icon: <AlertTriangle className="h-[18px] w-[18px]" />, tile: '#DC2626' },
  account: { label: 'Account', icon: <PlugZap className="h-[18px] w-[18px]" />, tile: '#D97706' },
  workspace: { label: 'Workspace', icon: <Building2 className="h-[18px] w-[18px]" />, tile: '#4F46E5' },
};

// Motion profiles, matching the desktop island (Settings → Notifications → Animation).
const MOTION = {
  smooth: { transition: { type: 'spring', stiffness: 380, damping: 36, mass: 0.9 }, travel: -48 },
  lively: { transition: { type: 'spring', stiffness: 520, damping: 30, mass: 0.9 }, travel: -70 },
  subtle: { transition: { type: 'tween', duration: 0.26, ease: [0.2, 0.8, 0.2, 1] }, travel: -12 },
} as const;
const CARD_BG: Record<NotificationPrefs['islandStyle'], string> = {
  black: '#0A0A0C',
  graphite: '#1D1D22',
  accent: 'linear-gradient(140deg, rgb(var(--primary) / 0.42) 0%, transparent 78%) #08080B', // tint over a solid base
};
type IslandLook = Pick<NotificationPrefs, 'islandPosition' | 'islandGap' | 'islandStyle' | 'islandMotion'>;

function targetFor(n: AppNotification): string {
  if (n.threadId) return `/inbox/${n.threadId}`;
  if (n.kind === 'account') return '/settings/accounts';
  if (n.kind === 'workspace') return '/settings/organization';
  if (n.kind === 'auto_reply' || n.kind === 'reminder' || n.kind === 'send_failed') return '/reminders';
  return '/inbox';
}

function Tile({ n }: { n: AppNotification }) {
  const k = KIND[n.kind];
  // Single new emails show the sender's initials on the account colour.
  if (n.kind === 'mail' && n.count === 1) {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] text-[0.8125rem] font-semibold text-white" style={{ background: n.accountColor ?? k.tile }}>
        {initials(n.title)}
      </span>
    );
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]" style={{ background: n.kind === 'mail' && n.accountColor ? n.accountColor : k.tile }}>
      {k.icon}
    </span>
  );
}

/** Circular countdown; finishing the CSS animation dismisses the notification. Pauses while hovered. */
function Countdown({ seconds, paused, onEnd }: { seconds: number; paused: boolean; onEnd: () => void }) {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5 -rotate-90" aria-hidden>
      <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="2.2" />
      <circle
        cx="10"
        cy="10"
        r="8"
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="2.2"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray="100"
        className="mp-island-countdown"
        style={{ animationDuration: `${seconds}s`, animationPlayState: paused ? 'paused' : 'running' }}
        onAnimationEnd={onEnd}
      />
    </svg>
  );
}

type Phase = 'pill' | 'open' | 'closing';

export function NotificationIsland({ current, queued, durationSeconds, look, onDone }: { current: AppNotification | null; queued: number; durationSeconds: number; look: IslandLook; onDone: () => void }) {
  const m = MOTION[look.islandMotion] ?? MOTION.smooth;
  const morph = m.transition;
  const navigate = useNavigate();
  const { accountId, setAccountId } = useAccountFilter();
  const [shown, setShown] = useState<AppNotification | null>(null);
  const [phase, setPhase] = useState<Phase>('pill');
  const [hover, setHover] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (current) {
      const fresh = !shown;
      setShown(current);
      if (fresh || phase === 'closing') {
        // Drop in as a pill, then bloom into the card.
        setPhase('pill');
        timer.current = setTimeout(() => setPhase('open'), 280);
      }
    } else if (shown) {
      setHover(false);
      setPhase('closing');
      timer.current = setTimeout(() => setShown(null), 240);
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (n: AppNotification) => {
    if (n.mailboxId && accountId && accountId !== n.mailboxId) setAccountId(n.mailboxId);
    navigate(targetFor(n));
    onDone();
  };

  const expanded = phase === 'open' && !!shown;
  const k = shown ? KIND[shown.kind] : null;
  const kicker = shown && k ? (shown.kind === 'mail' && shown.count > 1 ? `${shown.count} new emails` : k.label) : '';

  return (
    <div
      className={clsx('pointer-events-none fixed inset-x-0 top-0 z-[150] flex px-3', look.islandPosition === 'right' ? 'justify-end sm:pr-4' : 'justify-center')}
      style={{ paddingTop: `max(${ISLAND_GAP_PX[look.islandGap] ?? 16}px, env(safe-area-inset-top))` }}
    >
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {shown ? `${kicker}: ${shown.title}. ${shown.body}` : ''}
      </div>
      <AnimatePresence>
        {shown && k && (
          <motion.div
            key="island"
            layout
            initial={{ y: m.travel, opacity: 0, scale: look.islandMotion === 'subtle' ? 0.96 : 0.8 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: m.travel, opacity: 0, scale: look.islandMotion === 'subtle' ? 0.97 : 0.8, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }}
            transition={morph}
            style={{ borderRadius: expanded ? 28 : 22, background: CARD_BG[look.islandStyle] ?? CARD_BG.black }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setHover(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onDone();
            }}
            className={clsx(
              'pointer-events-auto overflow-hidden text-white shadow-[0_18px_50px_-12px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.08)_inset] transition-shadow duration-500',
              expanded ? 'w-[min(460px,calc(100vw-24px))]' : 'h-9 w-[132px]',
            )}
          >
            {expanded ? (
              <motion.div layout="position" className="relative">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.button
                    key={`${shown.id}-${shown.count}`}
                    type="button"
                    initial={{ opacity: 0, y: 6, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
                    transition={{ duration: 0.22 }}
                    onClick={() => open(shown)}
                    className="flex w-full items-start gap-3 px-3.5 pb-3 pt-3 text-left focus:outline-none"
                    aria-label={`Open: ${shown.title}`}
                  >
                    <Tile n={shown} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-white/55">
                        {shown.accountLabel && (
                          <>
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: shown.accountColor ?? '#fff' }} aria-hidden />
                            <span className="max-w-[9rem] truncate normal-case tracking-normal text-white/70">{shown.accountLabel}</span>
                            <span aria-hidden>·</span>
                          </>
                        )}
                        <span className="truncate">{kicker}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-[0.9375rem] font-semibold leading-snug">{shown.title}</span>
                      <span className="block truncate text-[0.8125rem] leading-snug text-white/75">{shown.body}</span>
                      {shown.detail && <span className="mt-0.5 block truncate text-xs text-white/50">{shown.detail}</span>}
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
                      <Countdown key={`${shown.id}-${shown.count}`} seconds={durationSeconds} paused={hover} onEnd={onDone} />
                      {queued > 0 && <span className="rounded-full bg-white/15 px-1.5 text-[0.625rem] font-semibold leading-4 text-white/85">+{queued}</span>}
                    </span>
                  </motion.button>
                </AnimatePresence>
                <AnimatePresence initial={false}>
                  {hover && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="flex gap-2 px-3.5 pb-3.5">
                        <button
                          type="button"
                          onClick={() => open(shown)}
                          className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-white text-[0.8125rem] font-semibold text-black transition-transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <ArrowUpRight className="h-4 w-4" /> {shown.threadId ? 'Open conversation' : 'Open'}
                        </button>
                        <button
                          type="button"
                          onClick={onDone}
                          className="flex h-9 items-center justify-center gap-1.5 rounded-full bg-white/12 px-4 text-[0.8125rem] font-medium text-white transition-colors hover:bg-white/20"
                        >
                          <X className="h-4 w-4" /> Dismiss
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div layout="position" className="flex h-9 items-center justify-between px-2.5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <span className="flex h-5 w-5 items-center justify-center rounded-full text-white" style={{ background: shown.kind === 'mail' ? shown.accountColor ?? k.tile : k.tile }}>
                  <span className="scale-[0.7]">{k.icon}</span>
                </span>
                <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" aria-hidden />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
